/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `length` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class LengthMesg : Mesg {
    public constructor() : super("Length", Profile.MesgNum.LENGTH)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** timestamp. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** event. */
    public var event: Event?
        get() = (getFieldValue(EVENT_FIELD_NUM) as? UByte)?.let { Event.fromValue(it) }
        set(value) = setFieldValue(EVENT_FIELD_NUM, value?.value)

    /** event_type. */
    public var eventType: EventType?
        get() = (getFieldValue(EVENT_TYPE_FIELD_NUM) as? UByte)?.let { EventType.fromValue(it) }
        set(value) = setFieldValue(EVENT_TYPE_FIELD_NUM, value?.value)

    /** start_time. */
    public var startTime: Instant?
        get() = (getFieldValue(START_TIME_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(START_TIME_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** total_elapsed_time, in s. */
    public var totalElapsedTime: Double?
        get() = getFieldValue(TOTAL_ELAPSED_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_ELAPSED_TIME_FIELD_NUM, value)

    /** total_timer_time, in s. */
    public var totalTimerTime: Double?
        get() = getFieldValue(TOTAL_TIMER_TIME_FIELD_NUM) as? Double
        set(value) = setFieldValue(TOTAL_TIMER_TIME_FIELD_NUM, value)

    /** total_strokes, in strokes. */
    public var totalStrokes: UShort?
        get() = getFieldValue(TOTAL_STROKES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TOTAL_STROKES_FIELD_NUM, value)

    /** avg_speed, in m/s. */
    public var avgSpeed: Double?
        get() = getFieldValue(AVG_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(AVG_SPEED_FIELD_NUM, value)

    /** swim_stroke, in swim_stroke. */
    public var swimStroke: SwimStroke?
        get() = (getFieldValue(SWIM_STROKE_FIELD_NUM) as? UByte)?.let { SwimStroke.fromValue(it) }
        set(value) = setFieldValue(SWIM_STROKE_FIELD_NUM, value?.value)

    /** avg_swimming_cadence, in strokes/min. */
    public var avgSwimmingCadence: UByte?
        get() = getFieldValue(AVG_SWIMMING_CADENCE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(AVG_SWIMMING_CADENCE_FIELD_NUM, value)

    /** event_group. */
    public var eventGroup: UByte?
        get() = getFieldValue(EVENT_GROUP_FIELD_NUM) as? UByte
        set(value) = setFieldValue(EVENT_GROUP_FIELD_NUM, value)

    /** total_calories, in kcal. */
    public var totalCalories: UShort?
        get() = getFieldValue(TOTAL_CALORIES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TOTAL_CALORIES_FIELD_NUM, value)

    /** length_type. */
    public var lengthType: LengthType?
        get() = (getFieldValue(LENGTH_TYPE_FIELD_NUM) as? UByte)?.let { LengthType.fromValue(it) }
        set(value) = setFieldValue(LENGTH_TYPE_FIELD_NUM, value?.value)

    /** player_score. */
    public var playerScore: UShort?
        get() = getFieldValue(PLAYER_SCORE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(PLAYER_SCORE_FIELD_NUM, value)

    /** opponent_score. */
    public var opponentScore: UShort?
        get() = getFieldValue(OPPONENT_SCORE_FIELD_NUM) as? UShort
        set(value) = setFieldValue(OPPONENT_SCORE_FIELD_NUM, value)

    /** Number of values stored in stroke_count. */
    public val numStrokeCount: Int
        get() = getField(STROKE_COUNT_FIELD_NUM)?.numValues ?: 0

    /** stroke_count, in counts. stroke_type enum used as the index */
    public var strokeCount: List<UShort?>?
        get() {
            val field = getField(STROKE_COUNT_FIELD_NUM) ?: return null
            return List(field.numValues) { getStrokeCount(it) }
        }
        set(value) {
            removeField(STROKE_COUNT_FIELD_NUM)
            value?.forEachIndexed { index, element -> setStrokeCount(index, element) }
        }

    public fun getStrokeCount(index: Int): UShort? =
        getFieldValue(STROKE_COUNT_FIELD_NUM, index) as? UShort

    public fun setStrokeCount(index: Int, value: UShort?) {
        setFieldValue(STROKE_COUNT_FIELD_NUM, value, index)
    }

    /** Number of values stored in zone_count. */
    public val numZoneCount: Int
        get() = getField(ZONE_COUNT_FIELD_NUM)?.numValues ?: 0

    /** zone_count, in counts. zone number used as the index */
    public var zoneCount: List<UShort?>?
        get() {
            val field = getField(ZONE_COUNT_FIELD_NUM) ?: return null
            return List(field.numValues) { getZoneCount(it) }
        }
        set(value) {
            removeField(ZONE_COUNT_FIELD_NUM)
            value?.forEachIndexed { index, element -> setZoneCount(index, element) }
        }

    public fun getZoneCount(index: Int): UShort? =
        getFieldValue(ZONE_COUNT_FIELD_NUM, index) as? UShort

    public fun setZoneCount(index: Int, value: UShort?) {
        setFieldValue(ZONE_COUNT_FIELD_NUM, value, index)
    }

    /** enhanced_avg_respiration_rate, in Breaths/min. */
    public var enhancedAvgRespirationRate: Double?
        get() = getFieldValue(ENHANCED_AVG_RESPIRATION_RATE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_AVG_RESPIRATION_RATE_FIELD_NUM, value)

    /** enhanced_max_respiration_rate, in Breaths/min. */
    public var enhancedMaxRespirationRate: Double?
        get() = getFieldValue(ENHANCED_MAX_RESPIRATION_RATE_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_MAX_RESPIRATION_RATE_FIELD_NUM, value)

    /** avg_respiration_rate. */
    public var avgRespirationRate: UByte?
        get() = getFieldValue(AVG_RESPIRATION_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(AVG_RESPIRATION_RATE_FIELD_NUM, value)

    /** max_respiration_rate. */
    public var maxRespirationRate: UByte?
        get() = getFieldValue(MAX_RESPIRATION_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(MAX_RESPIRATION_RATE_FIELD_NUM, value)

    /** metabolic_calories, in kcal. */
    public var metabolicCalories: UShort?
        get() = getFieldValue(METABOLIC_CALORIES_FIELD_NUM) as? UShort
        set(value) = setFieldValue(METABOLIC_CALORIES_FIELD_NUM, value)

    public companion object {
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val EVENT_FIELD_NUM: UByte = 0u
        public const val EVENT_TYPE_FIELD_NUM: UByte = 1u
        public const val START_TIME_FIELD_NUM: UByte = 2u
        public const val TOTAL_ELAPSED_TIME_FIELD_NUM: UByte = 3u
        public const val TOTAL_TIMER_TIME_FIELD_NUM: UByte = 4u
        public const val TOTAL_STROKES_FIELD_NUM: UByte = 5u
        public const val AVG_SPEED_FIELD_NUM: UByte = 6u
        public const val SWIM_STROKE_FIELD_NUM: UByte = 7u
        public const val AVG_SWIMMING_CADENCE_FIELD_NUM: UByte = 9u
        public const val EVENT_GROUP_FIELD_NUM: UByte = 10u
        public const val TOTAL_CALORIES_FIELD_NUM: UByte = 11u
        public const val LENGTH_TYPE_FIELD_NUM: UByte = 12u
        public const val PLAYER_SCORE_FIELD_NUM: UByte = 18u
        public const val OPPONENT_SCORE_FIELD_NUM: UByte = 19u
        public const val STROKE_COUNT_FIELD_NUM: UByte = 20u
        public const val ZONE_COUNT_FIELD_NUM: UByte = 21u
        public const val ENHANCED_AVG_RESPIRATION_RATE_FIELD_NUM: UByte = 22u
        public const val ENHANCED_MAX_RESPIRATION_RATE_FIELD_NUM: UByte = 23u
        public const val AVG_RESPIRATION_RATE_FIELD_NUM: UByte = 24u
        public const val MAX_RESPIRATION_RATE_FIELD_NUM: UByte = 25u
        public const val METABOLIC_CALORIES_FIELD_NUM: UByte = 26u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[0] = Field("Event", 0u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[1] = Field("EventType", 1u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[2] = Field("StartTime", 2u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[3] = Field("TotalElapsedTime", 3u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[4] = Field("TotalTimerTime", 4u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[5] = Field("TotalStrokes", 5u, BaseType.UINT16, 1.0, 0.0, "strokes", false)
            fields[6] = Field("AvgSpeed", 6u, BaseType.UINT16, 1000.0, 0.0, "m/s", false)
            fields[7] = Field("SwimStroke", 7u, BaseType.ENUM, 1.0, 0.0, "swim_stroke", false)
            fields[9] = Field("AvgSwimmingCadence", 9u, BaseType.UINT8, 1.0, 0.0, "strokes/min", false)
            fields[10] = Field("EventGroup", 10u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[11] = Field("TotalCalories", 11u, BaseType.UINT16, 1.0, 0.0, "kcal", false)
            fields[12] = Field("LengthType", 12u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[18] = Field("PlayerScore", 18u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[19] = Field("OpponentScore", 19u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[20] = Field("StrokeCount", 20u, BaseType.UINT16, 1.0, 0.0, "counts", false)
            fields[21] = Field("ZoneCount", 21u, BaseType.UINT16, 1.0, 0.0, "counts", false)
            fields[22] = Field("EnhancedAvgRespirationRate", 22u, BaseType.UINT16, 100.0, 0.0, "Breaths/min", false)
            fields[23] = Field("EnhancedMaxRespirationRate", 23u, BaseType.UINT16, 100.0, 0.0, "Breaths/min", false)
            fields[24] = Field("AvgRespirationRate", 24u, BaseType.UINT8, 1.0, 0.0, "", false)
                .addComponent(FieldComponent(22u, false, 8, 1.0, 0.0))
            fields[25] = Field("MaxRespirationRate", 25u, BaseType.UINT8, 1.0, 0.0, "", false)
                .addComponent(FieldComponent(23u, false, 8, 1.0, 0.0))
            fields[26] = Field("MetabolicCalories", 26u, BaseType.UINT16, 1.0, 0.0, "kcal", false)
            return fields
        }
    }
}
